import { useEffect, useState } from 'react';
import styled from 'styled-components'
import { voiceCard } from '../data/voiceCard';
import Card from '../components/molecules/Card';
import DefaultLayout from '../components/molecules/common/DefaultLayout';
import Text from '../components/atoms/Text';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import { useWordRecorder } from '../hooks/useWordRecorder';
import RecordVoiceModal from '../components/molecules/RecordVoiceModal';
import CardWordInput from '../components/atoms/CardWordInput';
import MainButton from '../components/atoms/MainButton';
import { useBack } from '../hooks/useBack';
import { useModal } from '../hooks/useModal';
import ExitModal from '../components/molecules/common/ExitModal';
import { useNavigate } from 'react-router-dom';
import { registerVoice } from '../api/auth';
import LoadingOverlay from '../components/molecules/common/LoadingOverlay';
import { tutorialImages } from '../data/tutorialImages';
import TutorialOverlay from '../components/molecules/auth/TutorialOverlay';
import { logEvent } from '../utils/eventLogger';

const Wrapper = styled.div`
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`
const CardIndex = styled.div`
  margin: 24px 0 8px 0;
`
const MobileBr = styled.br`
  display: none;
  @media (min-width: 410px) {
    display: inline;
  }
`
const GuideMessage = styled.div`
  text-align: left;
  word-break: keep-all;
  margin: 8px 0 16px 0;
`
const CardWrapper = styled.div`
  margin-bottom: 32px;
  width: 100%;
`
const MicButton = styled.button`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${(props) => props.theme.colors.secondary};
  border: none;
  cursor: pointer;
`
const MicIcon = styled(MicRoundedIcon)`
  color: ${(props) => props.theme.colors.text.inverse};
`
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 600px; // Wrapper 최대 폭과 맞춤
  padding: 0 20px;
`

const VoiceRegister = ({ next, onComplete }) => {
  // 튜토리얼
  const [showTutorial, setShowTutorial] = useState(true);
  const datas = tutorialImages.CHILD_VOICE_REGISTER.steps;

  const [isSubmitting, setIsSubmitting] = useState(false); // api 호출 상태
  const [showLoading, setShowLoading] = useState(false); // 화면에 로딩 오버레이 렌더링 여부 

  // 랜덤 카드 5장
  const [cards, setCards] = useState([]); 

  const [cardsData, setCardsData] = useState([])

  // 현재 보여지는 카드
  const [currentIndex, setCurrentIndex] = useState(0);

  // 카드 초기화
  const cardInit = () => {
    // 랜덤 카드 5장 생성
    // const shuffled = [...voiceCard].sort(() => 0.5 - Math.random()).slice(0, 5);
    
    // 랜덤 없이 voiceCard 배열의 처음부터 5장 순서대로 가져오기
    const sequenced = [...voiceCard].slice(0, 5);

    setCards(sequenced);
    // 랜덤 카드 5장에 대한 상태관리
    setCardsData(sequenced.map(card => ({
      imageId: card.name,
      audioBlob: null,
      meaning: card.name,
      kr_transcript: "",
      eng_transcript: "",
      ipa: ""
    })));
    // 현재 카드 인덱스 초기화
    setCurrentIndex(0)
  }

  // 페이지 처음 로드 시, 카드 초기화
  useEffect(() => {
    cardInit();
  }, [])

  // 새로고침 감시 시, 카드 초기화
  // useEffect(() => {
  //   const entries = performance.getEntriesByType("navigation");
  //   const isReload = entries[0]?.type === "reload";

  //   if (isReload) {
  //     cardInit();
  //   }
  // }, []);

  const [status, setStatus] = useState("idle")  
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { startRecording, cancelRecording } = useWordRecorder({
    onStatusChange: (s) => {
      setStatus(s);
      // 녹음 시작 시 모달 오픈
      if (s === "recording") setIsModalOpen(true);
      // 처리 완료 후 idle이면 모달 닫기
      if (s === "idle") setIsModalOpen(false);
    },
    autoStopOnSilence: true
  });
  
  const handleMicClick = async () => {
    try {
      // startRecording 호출 → 권한 요청, 녹음 시작
      const rlt = await startRecording();
      // console.log(rlt)
      // 취소 버튼을 눌러서 rlt가 null로 돌아온 경우, 아무것도 하지 않고 즉시 종료
      if (rlt === null) {
        return; 
      }
      // 녹음이 실제 시작된 경우에만 cardsData 업데이트
      setCardsData(prev => {
        const newData = [...prev];
        newData[currentIndex] = { ...newData[currentIndex], ...rlt, imageId: cards[currentIndex].name };
        return newData;
      });

      // 모달은 startRecording 안에서 onStatusChange("recording") 호출 시 자동으로 열림
    } catch (err) {
      console.error(err);

      // 추후 마이크 권한 거부, 장치 없음, 녹음 중 실패 등 오류 메시지 분기 처리 필요
      alert("브라우저 설정에서 마이크 접근을 허용한 후 다시 시도해주세요.");
    }
  };

  const handleNext = () => {
    setCurrentIndex(prev => prev + 1);
  };

  const handleSubmit = async () => {
    let delayTimer;

    try {
      // 로딩 오버레이 렌더링
      setIsSubmitting(true);
      delayTimer = setTimeout(() => {
        setShowLoading(true);
      }, 100);
      setShowLoading(true);
      await new Promise(requestAnimationFrame);
      
      // formData 구성
      const formData = new FormData();
      // audioFiles 추가
      cardsData.forEach((card, index) => {
        const extension = card.audioBlob.type.split("/")[1].split(";")[0];
        formData.append("audioFiles", card.audioBlob, `recording_${index}.${extension}`);
      });
      
      // metadatas 추가
      const metadatas = cardsData.map(card => ({
        kr_transcript: card.kr_transcript,
        eng_transcript: card.eng_transcript,
        ipa: card.ipa,
        meaning: card.meaning,
        image_id: card.imageId,
      }));

      formData.append(
        "metadatas",
        new Blob([JSON.stringify(metadatas)], { type: "application/json" })
      );

      setShowLoading(true);
      const res = await registerVoice(formData);
      // console.log("등록 성공", res);

      // 로그 기록
      logEvent("voice_embedding_save");
      
      if (onComplete) onComplete();
      navigate(next)
    } catch (err) {
      console.error("등록 실패", err);
      alert("등록에 실패했습니다. 다시 시도해주세요.");
    } finally {
      clearTimeout(delayTimer);
      setShowLoading(false);
      setIsSubmitting(false);
    }
  };

  const currentCardData = cardsData[currentIndex];
  const isLastCard = currentIndex === cards.length - 1;
  const buttonProps = isLastCard
    ? { title: "완료", onClick: handleSubmit }
    : { title: "다음 단어", onClick: handleNext };

  const navigate = useNavigate();
  const goBack = useBack(); // 뒤로가기 훅 호출
  const { open, close, isOpen, modalType } = useModal(); // 모달 훅 호출
  
  const handleExit = () => {
    close();
    goBack();
  };

  const handleBack = () => {
    open("exit")
  }

  const handleCancel = () => {
    cancelRecording(); // 훅 내부 정리
    setIsModalOpen(false);
  }

  return (
    <DefaultLayout
      headerType="sub"
      title="아이 음성 등록"
      onBack={handleBack}
      bgColor="gray">
      <Wrapper>
        <CardIndex>
          <Text variant="headline3" weight="medium" color="accent">{currentIndex + 1}</Text>
          <Text variant="headline3" weight="medium" color="alternative"> / {cards.length}</Text>
        </CardIndex>
        <GuideMessage>
          <Text variant="body2" color="alternative">
            제시된 낱말카드와 다른 발음이나 표현들이 녹음돼도 괜찮아요. <MobileBr />
            아이 목소리 외 다른 소리가 들어가지 않도록 주의해주세요.
          </Text>
        </GuideMessage> 
        <CardWrapper>
          {cards.length > 0 && currentIndex < cards.length && (
            <Card card={cards[currentIndex]}/>
          )}
        </CardWrapper>
    
        {!currentCardData?.kr_transcript && (
          <MicButton onClick={handleMicClick}>
            <MicIcon sx={{ fontSize:36 }}/>
          </MicButton>
        )}

        {currentCardData?.kr_transcript && (
        <>
          <CardWordInput value={currentCardData.kr_transcript}/>
          <ButtonWrapper>
            <MainButton
              $variant="primary"
              title="다시 녹음" 
              onClick={handleMicClick}/>
            <MainButton
              $variant="secondary"
              {...buttonProps}/>
          </ButtonWrapper>
        </>
        )}
      
      </Wrapper>
      {isModalOpen && 
        <RecordVoiceModal 
          status={status}
          onRetry={handleMicClick}
          onCancel={handleCancel} />}
      {isOpen && modalType === 'exit' && (
        <ExitModal
          title="음성 등록을 중단할까요?"
          onClose={close}
          onConfirm={handleExit}/>
      )}
      {showLoading && <LoadingOverlay/>}
      {showTutorial && 
        <TutorialOverlay 
          datas={datas} 
          onClose={() => setShowTutorial(false)}
        />
      }
    </DefaultLayout>


  )
}

export default VoiceRegister
